import dayjs from 'dayjs';
import * as React from 'react';
import { Flipper } from 'react-flip-toolkit';
import { ProFilter } from 'react-pro-components';
import { ProFilterProps } from 'react-pro-components/typings/components/pro-filter/types';
import { Form, Skeleton } from 'ssc-ui-react';
import { IconHelpOutline } from 'ssc-ui-react/es/components/icon';

import { ReactComponent as TvIcon } from '@/assets/svg/tv-bold.svg';
import { $gt } from '@/common/appCtx';
import Empty from '@/pages/workstation-dashboard/components/empty';
import { useWorkforceEnums } from '@/store/workforce-enums';
import { getSelectOptions } from '@/utils/safe-get';

import {
  CacheType,
  CalculateMethod,
  CalculateMethodMaxText,
  CalculateMethodMinText,
  CalculateMethodSumText,
  VISIBLE_GROUPS
} from '../../const';
import { OriginGroupItem } from '../../defined';
import useDropdownListStore from '../../store/use-dropdown-list';
import useWorkstationOverviewStore from '../../store/use-workstation-overview';
import { dashboardDBManager } from '../../utils/storage';
import Collapse from '../collapse';
import Group from './group';
import styles from './index.module.less';

type FormType = {
  activity_type_list?: number[];
  workstation_group_id_list?: number[];
}

type StorageType = {
  activity_type_list?: { value: number; label: string }[];
  workstation_group_list?: { value: number; label: string }[];
}

const GroupCards: React.FC = () => {
  const [form] = Form.useForm();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const setTargetGroup = useWorkstationOverviewStore.use.setTargetGroup();
  const groupList = useWorkstationOverviewStore.use.groupList();
  const setGroupList = useWorkstationOverviewStore.use.setGroupList();
  const targetGroup = useWorkstationOverviewStore.use.targetGroup();
  const lastUpdateData = useWorkstationOverviewStore.use.lastUpdate();
  const groupListLoading = useWorkstationOverviewStore.use.groupListLoading();
  const getGroupListAsync = useWorkstationOverviewStore.use.getGroupListAsync();
  const calculateMethod = useWorkstationOverviewStore.use.calculateMethod?.();
  const groupDropdownListLoading = useDropdownListStore.use.lastHourGroupDropdownListLoading();
  const groupDropdownList = useDropdownListStore.use.lastHourGroupDropdownList?.();
  const groupDropdownListMap = useDropdownListStore.use.lastHourGroupDropdownListMap?.();
  const setOverviewForm = useWorkstationOverviewStore.use.setOverviewForm();
  const setLastHourGroupDropdownList = useDropdownListStore.use.setLastHourGroupDropdownList();
  const lastFetchUpdateTime = useWorkstationOverviewStore.use.lastFetchUpdateTime();
  const enums = useWorkforceEnums.use.enums();
  const activityTypeOptions = getSelectOptions(enums, { dataPath: 'workstation_type' }) as { value: number; label: string }[];

  const setCache = React.useCallback((values: FormType) => {
    const { activity_type_list, workstation_group_id_list } = values;
    const cacheValue = {
      activity_type_list,
      workstation_group_list: workstation_group_id_list?.map((item) => {
        return {
          value: item,
          label: groupDropdownListMap[item],
        };
      }),
    };
    dashboardDBManager.put(CacheType.GroupThroughputOverview, cacheValue);
  }, [groupDropdownListMap]);

  const getCache = React.useCallback(async () => {
    try {
      const cache = await dashboardDBManager.get(CacheType.GroupThroughputOverview) as StorageType | undefined;
      const workstation_group_id_list: number[] = [];
      const disabledWorkstationGroupIdList: {value: number; label: string; disabled?: boolean}[] = [];
      cache?.workstation_group_list?.forEach(item => {
        if (!groupDropdownListMap[item.value]) {
          disabledWorkstationGroupIdList.push({ ...item, disabled: true });
        }
        workstation_group_id_list.push(item.value);
      });
      setLastHourGroupDropdownList(disabledWorkstationGroupIdList, true);
      form.setFieldValue('workstation_group_id_list', workstation_group_id_list.length ? workstation_group_id_list : undefined);
      form.setFieldValue('activity_type_list', cache?.activity_type_list);
    } catch (e) {
      console.error('[GroupThroughputOverview] getCache error: ', e);
    } finally {
      setOverviewForm(form);
    }
  }, [setLastHourGroupDropdownList, form, groupDropdownListMap, setOverviewForm]);

  React.useEffect(() => {
    getCache();
  }, [getCache]);

  const totalGroups = React.useMemo(() => groupList?.length ?? 0, [groupList]);

  const lastUpdate = React.useMemo(() => {
    if (!lastFetchUpdateTime) return '-';
    return dayjs(lastFetchUpdateTime * 1000).format('YYYY/MM/DD HH:mm:ss');
  }, [lastFetchUpdateTime]);

  const timeBeginStr = React.useMemo(() => {
    if (!lastUpdateData?.startTime) return '';
    return dayjs(lastUpdateData?.startTime * 1000).format('D MMM HH:mm');
  }, [lastUpdateData]);

  const timeEndStr = React.useMemo(() => {
    if (!lastUpdateData?.endTime) return '';
    return dayjs(lastUpdateData?.endTime * 1000).format('D MMM HH:mm');
  }, [lastUpdateData]);

  const proFilterProps = React.useMemo(() => {
    return {
      formProps: { form },
      columns: 3,
      fields: [
        {
          label: $gt('Activity Type'),
          type: 'select',
          name: 'activity_type_list',
          ctrlProps: {
            mode: 'multiple',
            maxTagCount: 1,
            maxTagTextWidth: 80,
            filterOption: true,
            allowClear: true,
            options: activityTypeOptions,
          },
        },
        {
          label: $gt('Group ID/Name'),
          type: 'select',
          name: 'workstation_group_id_list',
          ctrlProps: {
            mode: 'multiple',
            maxTagCount: 1,
            maxTagTextWidth: 80,
            filterOption: true,
            allowClear: true,
            loading: groupDropdownListLoading,
            options: groupDropdownList,
          },
        },
      ],
      footerProps:{
        submitButtonProps: {
          loading: groupListLoading,
        },
      },
      onChange: (values) => {
        setCache(values);
      },
      onSubmit: (values) => {
        getGroupListAsync(values);
      },
      onReset: () => {
        form.resetFields();
        getGroupListAsync({});
      },
    } as ProFilterProps<any>;
  }, [activityTypeOptions, form, getGroupListAsync, groupDropdownList, groupDropdownListLoading, groupListLoading, setCache]);

  const handleGroupSelect = (index: number) => {
    if (!groupList || !groupList?.length) return;
    const newGroupList = groupList.slice();
    newGroupList.splice(index, 1);
    setGroupList([groupList[index], ...newGroupList]);

    setTargetGroup({
      groupId: groupList?.[index].workstation_group_id || 0,
      groupName: groupList?.[index].workstation_group_name || '',
      workstation_group: groupList?.[index].workstation_group || '',
      groupIndex: 0,
    });
  };

  const throughputOverviewTooltip = React.useMemo(() => {
    if (calculateMethod === CalculateMethod.Min) {
      return CalculateMethodMinText;
    } else if (calculateMethod === CalculateMethod.Max) {
      return CalculateMethodMaxText;
    } else if (calculateMethod === CalculateMethod.Sum) {
      return CalculateMethodSumText;
    }
    return null;
  }, [calculateMethod]);

  const handleTvMode = (group: OriginGroupItem) => {
    const params = `type=single&workstation_group=${group.workstation_group}&workstation_group_id=${group.workstation_group_id}&workstation_group_name=${group.workstation_group_name}`;
    const url = `/#/workforceops/workstation-dashboard/tv-mode?${params}`;
    window.open(url, '_blank');
  };

  const handleGroupTvMode = () => {
    const firstGroup = groupList?.[0];
    const values = form.getFieldsValue();
    let extraParams = '';
    if (values.activity_type_list) {
      extraParams += `&activity_type_list=${values.activity_type_list}`;
    }
    if (values.workstation_group_id_list) {
      extraParams += `&workstation_group_id_list=${values.workstation_group_id_list}`;
    }
    const params = `type=group&workstation_group=${firstGroup?.workstation_group}&workstation_group_id=${firstGroup?.workstation_group_id}&workstation_group_name=${firstGroup?.workstation_group_name}${extraParams}`;
    const url = `/#/workforceops/workstation-dashboard/tv-mode?${params}`;
    window.open(url, '_blank');
  };

  const renderGroups = (start: number, end: number) => {
    return groupList?.slice(0, end).map((group, index) => {
      const groupIndex = start + index;
      return (
        <div className={styles.groupWrapper}>
          <Group
            selected={groupIndex === targetGroup?.groupIndex}
            title={group.workstation_group ?? ''}
            orderCount={group.total_throughput || 0}
            workstationCount={group.total_active_workstation_count || 0}
            operatorCount={group.total_active_ops_count || 0}
            interactiveTypeThroughputList={group.interactive_type_throughput_list || []}
            nonInteractiveTypeThroughputList={group.non_interactive_type_throughput_list || []}
            onSelected={() => handleGroupSelect(groupIndex)}
            onTvMode={() => handleTvMode(group)}
          />
        </div>
      );
    });
  };

  return (
    <>
      <div className={styles.groupCardHead}>
        <div className={styles.lastUpdate}>
          {$gt('Last Update')}: {lastUpdate}
        </div>
        <ProFilter {...proFilterProps} />
        <div className={styles.divider}></div>
      </div>
      <div className={styles.groupCards}>
        <div className={styles.throughput}>
          <div className={styles.titleWrapper}>
            <Form.Legend
              level={1}
              tooltip={{
                title: throughputOverviewTooltip,
                icon: <IconHelpOutline />,
              }}
            >
              {$gt('Group Throughput Overview')}
            </Form.Legend>
            <span className={styles.tips}>
              {$gt('Date from')} {timeBeginStr}～{timeEndStr}
            </span>
          </div>
          <div
            className={styles.tvMode}
            onClick={handleGroupTvMode}
          >
            <TvIcon />
            <span>
              {$gt('TV Mode')}
              ({groupList?.length})
            </span>
          </div>
        </div>
        <Skeleton
          loading={groupListLoading}
          paragraph={true}
        >
          <div className={styles.groupsWrapper}>
            <Flipper
              flipKey={groupList}
              staggerConfig={{
                default: {
                  speed: 1,
                  reverse: true
                }
              }}
              decisionData={{}}
            >
              <div className={styles.groups}>
                {renderGroups(0, isExpanded ? totalGroups : VISIBLE_GROUPS)}
              </div>
              {totalGroups <= 0 && (
                <div className={styles.empty}>
                  <Empty />
                </div>
              )}
            </Flipper>
            <Collapse
              style={{ marginTop: 16 }}
              isExpanded={isExpanded}
              rest={totalGroups - VISIBLE_GROUPS}
              onClick={(toggle) => {
                setIsExpanded(toggle);
              }}
            />
          </div>
        </Skeleton>
      </div>
    </>
  );
};

export default GroupCards;
